import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { Link } from 'react-router';
import { ShieldCheck } from 'lucide-react';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'detections', label: 'Detections & Content Packs' },
  { id: 'content-packs', label: 'Content Packs', level: 1 },
  { id: 'custom-detections', label: 'Custom & Sigma Import', level: 1 },
  { id: 'sequence-detections', label: 'Sequence Detections', level: 1 },
  { id: 'rba', label: 'Risk-Based Alerting & Notables' },
  { id: 'effective-score', label: 'Effective Risk Score', level: 1 },
  { id: 'risk-bands', label: 'Risk Bands', level: 1 },
  { id: 'tuning', label: 'Per-Org Tuning', level: 1 },
  { id: 'risk-explorer', label: 'Risk Events & Investigation' },
  { id: 'explorer', label: 'Risk Events Explorer', level: 1 },
  { id: 'risk-dashboard', label: 'Analyst Dashboard', level: 1 },
  { id: 'investigation-workflow', label: 'Investigation & Escalation', level: 1 },
  { id: 'threat-intel', label: 'Threat Intelligence' },
  { id: 'drop-feeds', label: 'Network & ASN Blocklists', level: 1 },
  { id: 'enrichment-commands', label: 'Enrichment Commands', level: 1 },
  { id: 'connectors', label: 'Cloud & Identity Connectors' },
  { id: 'ueba', label: 'UEBA / Behavioral Analytics' },
  { id: 'soar', label: 'Automated Response (SOAR)' },
  { id: 'response-actions', label: 'Response Actions', level: 1 },
  { id: 'playbooks', label: 'Playbooks', level: 1 },
  { id: 'mitre', label: 'MITRE ATT&CK Coverage' },
  { id: 'entity-360', label: 'Entity 360' },
  { id: 'compliance', label: 'Compliance Reporting' },
];

export function SecurityMonitoringPage() {
  useDocumentMeta({
    title: 'Security Monitoring (SIEM): LogPulse Docs',
    description:
      'LogPulse SIEM: AI-evaluated, EU-sovereign security monitoring built on LPQL and ClickHouse. Risk-based alerting, MITRE-tagged detections, Sigma import, threat intelligence, UEBA, and SOAR.',
  });

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
            Security
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Security Monitoring (SIEM)
          </h1>
          <P>
            LogPulse Security Monitoring turns your existing log estate into a full SIEM. It runs on
            the same LPQL query language and ClickHouse engine you already use for observability, so
            there is no separate data store to feed, learn, or pay for. Detections, AI verdicts, and
            behavioral analytics all converge on a risk-based alerting model that surfaces a small
            number of high-confidence notables instead of a flood of per-rule alerts.
          </P>
        </div>

        {/* Beta availability banner */}
        <div className="mb-10 rounded-xl border border-primary-200 dark:border-primary-900/50 bg-primary-50 dark:bg-primary-900/20 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold uppercase tracking-wider bg-primary-600 text-white px-2 py-0.5 rounded">
              Every plan
            </span>
          </div>
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            The Security Monitoring (SIEM) features described on this page are included in{' '}
            <strong>every plan, Free too</strong>. Paid plans add ingest volume, retention and higher limits.
            Questions? Reach out to{' '}
            <a
              href="mailto:sales@logpulse.io?subject=LogPulse%20Security"
              className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              sales@logpulse.io
            </a>
            .
          </p>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview">
          <P>
            The SIEM is AI-evaluated and EU-sovereign by design. Customer log data is stored in the
            EU (GCP Amsterdam) and never leaves the region for normal operation. AI evaluation runs
            on Amazon Bedrock in an EU region, so investigations and verdicts stay within European
            infrastructure.
          </P>
          <P>
            Because it is built on the same LPQL + ClickHouse engine as the rest of the platform,
            every detection is just an LPQL query on a schedule, and every alert is something you can
            click into, pivot from, and re-run interactively. The defining architectural choice is{' '}
            <strong>Risk-Based Alerting (RBA)</strong>: rather than paging on every rule match,
            detections contribute risk to the entities they touch (user, host, IP). When accumulated
            risk crosses a threshold, a single notable is opened for that entity.
          </P>
          <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'AI-evaluated', desc: 'Notables are auto-investigated by an LLM that triages and closes false positives before a human sees them.', color: 'text-info-500' },
              { title: 'EU-sovereign', desc: 'Log data in GCP Amsterdam; AI via Amazon Bedrock in the EU. No data egress for normal operation.', color: 'text-success-500' },
              { title: 'One engine', desc: 'Same LPQL + ClickHouse used for observability. Detections are LPQL queries; alerts are pivotable.', color: 'text-warning-500' },
              { title: 'Risk-based', desc: 'Every entity carries one bounded 0–100 risk score that rewards attack breadth and decays over time, not an alert per rule.', color: 'text-primary-500' },
            ].map((card) => (
              <div key={card.title} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h4 className={`font-semibold text-sm mb-1 ${card.color}`}>{card.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">{card.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Detections & content packs */}
        <Section id="detections" title="Detections & Content Packs">
          <P>
            LogPulse ships with 50+ built-in detections written in LPQL. Each detection is a
            scheduled query that runs on an interval, evaluates recent data, and emits risk events
            for the entities it matches. Detections are tagged with the relevant{' '}
            <strong>MITRE ATT&CK</strong> tactics and techniques so coverage can be measured and
            gaps identified.
          </P>

          <SubSection id="content-packs" title="Content Packs">
            <P>
              Built-in detections are grouped into content packs by domain. You can enable, disable,
              or tune packs independently so the SIEM only runs detections relevant to the data
              sources you actually ingest.
            </P>
            <Table
              headers={['Content Pack', 'Example Detections']}
              rows={[
                ['Authentication', 'Brute-force, password spraying, MFA fatigue, disabled-account login'],
                ['Web', 'SQL injection patterns, path traversal, suspicious user-agents, web shells'],
                ['Network', 'Port scans, beaconing, data exfiltration volume, rare destination ports'],
                ['DNS', 'DNS tunneling, DGA-style domains, high-entropy lookups, known-bad resolutions'],
                ['Cloud / IAM', 'Root/owner usage, policy changes, new access keys, MFA disabled on admin'],
                ['Endpoint', 'Suspicious process lineage, LOLBins, persistence, defense evasion'],
                ['Email', 'Mail-forwarding rules, mass external sends, suspicious OAuth grants'],
                ['Identity threats', 'Impossible travel, token theft, privilege escalation, dormant-account reactivation'],
              ]}
            />
          </SubSection>

          <SubSection id="custom-detections" title="Custom Detections & Sigma Import">
            <P>
              Any LPQL query can be saved as a custom detection with its own schedule, severity,
              risk score, and MITRE tags. If you already maintain detection content in{' '}
              <strong>Sigma</strong>, paste the Sigma YAML rule into the import dialog and LogPulse
              transpiles it to an equivalent LPQL detection that you can review and adjust before
              enabling.
            </P>
            <CodeBlock>{`# Custom detection: repeated failed admin logins from one source
auth.signin
| where outcome == "failure" and user.is_admin == true
| stats count() as failures by source.ip, user.name
| where failures >= 10`}</CodeBlock>
          </SubSection>

          <SubSection id="sequence-detections" title="Single vs. Sequence Detections">
            <P>
              Most detections are <strong>single-stage</strong>: one query, one condition, evaluated
              on each run. LogPulse also supports <strong>sequence (multi-stage) detections</strong>{' '}
              that correlate several ordered conditions for the same entity within a time window.
              This expresses attack chains that no single event reveals.
            </P>
            <P>
              For example, a sequence can require, for the same user within 15 minutes: a burst of
              failed logins, followed by a successful login, followed by a privilege-escalation
              event. Only when all stages occur in order does the sequence fire and emit risk.
            </P>
            <CodeBlock>{`# Sequence detection (multi-stage), per user, 15m window
sequence by user.name within 15m
  [ auth.signin where outcome == "failure" ] count >= 5
  [ auth.signin where outcome == "success" ]
  [ iam.role_change where action == "grant" and role.privileged == true ]`}</CodeBlock>
          </SubSection>
        </Section>

        {/* RBA & Notables */}
        <Section id="rba" title="Risk-Based Alerting & Notables">
          <P>
            Risk-Based Alerting is the core of the SIEM. Instead of firing an alert for every rule
            match, every signal, a detection match, an AI verdict, or an anomaly, emits a{' '}
            <strong>risk event</strong> attributed to one or more entities (user, host, IP). Risk
            accumulates on the entity over time, and only when the entity's risk crosses a threshold
            is a <strong>notable</strong> opened.
          </P>

          <SubSection id="effective-score" title="Effective Risk Score">
            <P>
              Every entity carries a single <strong>effective risk score from 0 to 100</strong>,
              the same number you see on the leaderboard, on the notable, and the one promotion is
              decided on. It is built so that the <em>shape</em> of an entity's activity, not just its
              volume, drives the score:
            </P>
            <Table
              headers={['Property', 'What it does']}
              rows={[
                ['Diminishing returns', 'The strongest signals count in full; repeats of the same low-level signal count for steadily less, so sheer volume saturates instead of piling up. A host hammered by one repetitive probe does not outrank a real multi-step intrusion.'],
                ['Attack-breadth boost', 'Activity that spans several MITRE ATT&CK tactics (stages of an attack) and distinct techniques is boosted. Breadth across the kill chain is weighted more heavily than variety within one stage, so genuine intrusions rise above single-signal noise.'],
                ['Time decay', 'Each signal decays on a half-life, so stale activity fades and only sustained or recent risk stays high.'],
                ['Entity criticality', 'A criticality multiplier weights the score by how important the entity is (for example a domain admin or a production host reaches a notable sooner).'],
                ['Bounded 0–100', 'Everything is compressed onto a fixed 0–100 scale, so scores are always comparable across entities and never run off the top.'],
              ]}
            />
            <P>
              Promotion is then <strong>one threshold on one score</strong>: when an entity's
              effective risk crosses it, a notable is opened. There is{' '}
              <strong>one open notable per entity</strong>. Additional risk events for an entity
              that already has an open notable are attached to it rather than creating duplicates,
              which keeps the analyst queue short and contextual. When the score decays back below a
              lower clear threshold, an automatically-raised notable resolves itself.
            </P>
          </SubSection>

          <SubSection id="risk-bands" title="Risk Bands">
            <P>
              The 0–100 effective score maps onto five severity bands, used consistently across the
              leaderboard, the notable, and notification severity:
            </P>
            <Table
              headers={['Band', 'Score', 'Reading']}
              rows={[
                ['Critical', '90–100', 'Broad, multi-stage activity on a high-value entity. Investigate first.'],
                ['High', '70–89', 'Strong, corroborated risk; typically where notables promote.'],
                ['Moderate', '40–69', 'Worth watching; single-stage or still-accumulating activity.'],
                ['Low', '20–39', 'Minor, isolated signals.'],
                ['Info', '0–19', 'Background noise; below the level of concern.'],
              ]}
            />
          </SubSection>

          <SubSection id="tuning" title="Per-Org Tuning">
            <P>
              The risk model is tunable per organization so it fits your environment and risk
              appetite. Configurable parameters include:
            </P>
            <Table
              headers={['Setting', 'Description']}
              rows={[
                ['Notable threshold', 'Effective risk score required to promote a notable; the clear threshold auto-resolves it once risk decays back below.'],
                ['Decay half-life', 'How quickly risk decays. Shorter favors recency, longer retains history.'],
                ['Volume sensitivity', 'How fast repeats of the same signal stop adding risk. Higher saturates noisy, repetitive activity sooner.'],
                ['Tactic & technique diversity boosts', 'How much each additional MITRE attack stage and technique lifts the score, with a cap so breadth alone cannot run away.'],
                ['Score saturation', 'How hard it is to reach the top of the 0–100 scale, the main dial for reserving high scores for the clearest threats.'],
                ['Criticality multipliers', 'Per-tier weights (for example ×2 for crown-jewel hosts).'],
              ]}
            />
            <Callout type="tip">
              The scoring constants ship with sensible defaults. Tune the saturation and threshold
              first against how scores land in your environment; the diversity and volume dials are
              for fine-grained shaping once the baseline feels right.
            </Callout>
          </SubSection>
        </Section>

        {/* Risk Events explorer & investigation */}
        <Section id="risk-explorer" title="Risk Events & Investigation">
          <P>
            Every signal that contributes risk is written as a <strong>risk event</strong> you can
            inspect, search, and pivot from. Around these sit an analyst dashboard for triage and a
            full investigation workflow on each notable.
          </P>

          <SubSection id="explorer" title="Risk Events Explorer">
            <P>
              The explorer is a searchable stream of every risk event across the organization, so an
              analyst can see exactly what is driving an entity's score. Filter by source engine
              (detection, sequence, anomaly, UEBA, threat-intel, AI evaluation), by entity, by MITRE
              tag, by score range, and by time window, then open any event to its underlying data.
            </P>
            <Callout type="tip">
              Every risk event and notable is deep-linkable. Paste a link into a ticket or a chat
              and a colleague lands on exactly the same filtered view.
            </Callout>
          </SubSection>

          <SubSection id="risk-dashboard" title="Analyst Dashboard">
            <P>
              A "where do I look first" dashboard sits on top of the risk stream, answering triage
              questions at a glance:
            </P>
            <Table
              headers={['View', 'Answers']}
              rows={[
                ['Top risk entities', 'A leaderboard ranked by effective risk score and band, the entities to investigate first.'],
                ['MITRE tactic breakdown', 'Which attack stages are most active across the window.'],
                ['Event volume timeline', 'How risk activity trends over time, to spot bursts.'],
                ['Key indicators', 'Headline counts: risk events, active entities, tactics in play, top entity risk.'],
              ]}
            />
          </SubSection>

          <SubSection id="investigation-workflow" title="Investigation & Escalation">
            <P>
              Each notable is a workspace, not just an alert. Beyond the AI auto-investigation (see{' '}
              <a href="#soar" className="text-primary-600 dark:text-primary-400 hover:underline">Automated Response</a>),
              analysts work the case directly:
            </P>
            <Table
              headers={['Action', 'What it does']}
              rows={[
                ['Live AI status', 'The notable shows whether its AI investigation is running or complete, and surfaces the verdict, summary, and recommendations once ready.'],
                ['Notes', 'An append-only, attributed comment trail for the forensic record of the investigation.'],
                ['Evidence', 'Pin contributing risk events onto the notable as a snapshot, so the evidence survives data retention and any later recomputation.'],
                ['Escalate', 'Promote a risk event from the explorer into a notable by hand. Analyst-raised notables are owned by a person and are exempt from auto-resolve. Only a human closes them.'],
              ]}
            />
          </SubSection>
        </Section>

        {/* Threat Intelligence */}
        <Section id="threat-intel" title="Threat Intelligence">
          <P>
            LogPulse includes a built-in IOC reputation store fed by free, reputable threat-intel
            feeds: AlienVault OTX, AbuseIPDB, the abuse.ch suite (Feodo Tracker, URLhaus and
            ThreatFox), and the Spamhaus DROP and ASN-DROP blocklists. The store is used to
            automatically enrich risk events. When a detection touches an IP that is known-bad, the
            risk is boosted and the resulting notable is tagged with the threat-intel match, so
            analysts see the reputation context without leaving the alert.
          </P>
          <P>
            On top of the feeds, each organization can maintain its own allow/deny{' '}
            <strong>watchlists</strong>. A deny-list entry forces a known-bad classification (for
            entities you have confirmed internally), while an allow-list entry suppresses noise from
            sources you know to be benign (for example, a scanning service you operate).
          </P>
          <Callout type="info">
            Some feeds use a free upstream API key (OTX, AbuseIPDB); the abuse.ch and Spamhaus feeds
            run automatically with no key required. Add any keys in the threat-intel settings to
            enable automatic feed refresh, a one-time configuration step.
          </Callout>

          <SubSection id="drop-feeds" title="Network & ASN Blocklists">
            <P>
              Most reputation feeds list individual indicators. The Spamhaus blocklists work at a
              coarser, higher-confidence level, whole ranges of internet infrastructure that are
              leased to or controlled by malicious actors:
            </P>
            <Table
              headers={['Feed', 'What it flags']}
              rows={[
                ['DROP', 'Network blocks (CIDR ranges) that are hijacked or rented by criminals. Any IP falling inside a listed block is treated as known-bad via a fast range match.'],
                ['ASN-DROP', 'Entire malicious autonomous systems (network operators). An IP is resolved to its owning ASN, and if that operator is listed the IP inherits the known-bad classification.'],
              ]}
            />
            <P>
              Both feed straight into the same enrichment path: a probe from an address inside a DROP
              block, or hosted by a DROP-listed network operator, boosts the risk on that risk event
              and tags the notable, so perimeter scanning from known-bad infrastructure surfaces as
              an attributed, threat-intel-backed notable rather than anonymous firewall noise.
            </P>
          </SubSection>

          <SubSection id="enrichment-commands" title="LPQL Enrichment Commands">
            <P>
              Two LPQL commands bring enrichment into any query, not just scheduled detections. Use
              them interactively while hunting, or inside a custom detection.
            </P>
            <P>
              <code>| threatintel &lt;ipField&gt;</code> (alias <code>| iplookup</code>) looks up the
              IP in the reputation store and adds reputation fields (verdict, source feed, score):
            </P>
            <CodeBlock>{`web.access
| where status >= 400
| threatintel source.ip
| where threat.verdict == "malicious"
| stats count() as hits by source.ip, threat.source, threat.score
| sort hits desc`}</CodeBlock>
            <P>
              <code>| iplocation &lt;ipField&gt;</code> resolves an IP to GeoIP attributes,
              country, city, latitude, longitude, and ASN:
            </P>
            <CodeBlock>{`auth.signin
| where outcome == "success"
| iplocation source.ip
| stats count() as logins by user.name, geo.country, geo.asn
| sort logins desc`}</CodeBlock>
            <Callout type="info">
              GeoIP resolution uses MaxMind GeoLite2 and requires a free MaxMind license key for the
              self-hosted database refresh. Add the key in settings to keep the GeoIP database
              current; without it, <code>iplocation</code> falls back to whatever database snapshot
              is present.
            </Callout>
          </SubSection>
        </Section>

        {/* Connectors */}
        <Section id="connectors" title="Cloud & Identity Connectors">
          <P>
            Security telemetry from cloud and identity providers is ingested through built-in
            connectors and normalized to canonical, CIM/OCSF-aligned data models. Normalization
            means a detection written against, say, an authentication model works across sources
            without rewriting field names per vendor.
          </P>
          <Table
            headers={['Connector', 'Data Ingested']}
            rows={[
              ['Microsoft Entra ID', 'Sign-in logs and audit logs (identity events, conditional access, role changes)'],
              ['Microsoft 365', 'Office 365 Management Activity (Exchange, SharePoint, Teams, admin activity)'],
              ['AWS CloudTrail', 'Management and data-plane API activity across AWS accounts'],
            ]}
          />
          <P>
            These join the existing platform connectors, Azure DevOps, GitHub, Railway, and
            Cloudflare, so application, infrastructure, and security data live side by side in the
            same engine.
          </P>
        </Section>

        {/* UEBA */}
        <Section id="ueba" title="UEBA / Behavioral Analytics">
          <P>
            User and Entity Behavior Analytics adds detections that depend on an entity's own
            history rather than a static rule. Findings flow into the same risk model and open or
            augment notables, tagged with a <code>behavior:*</code> label so they are easy to filter.
          </P>
          <Table
            headers={['Analytic', 'What It Detects']}
            rows={[
              ['Impossible travel', 'Geo velocity between consecutive sign-ins exceeds plausible travel speed.'],
              ['First-seen activity', 'A new country or IP seen for a user for the first time.'],
              ['Volume spikes', 'Activity for a user that exceeds a per-user baseline (e.g. download or query volume).'],
            ]}
          />
          <Callout type="tip">
            Because UEBA findings feed risk rather than paging directly, a single weak signal (a
            first-seen IP) rarely alerts on its own, but combined with other risk on the same user it
            can tip the entity over the notable threshold.
          </Callout>
        </Section>

        {/* SOAR */}
        <Section id="soar" title="Automated Response (SOAR)">
          <P>
            When a notable is opened, LogPulse runs an AI auto-investigation that gathers context,
            correlates the contributing risk events, and forms a verdict. Investigations that
            conclude a notable is benign close it automatically, keeping the analyst queue focused on
            real incidents.
          </P>

          <SubSection id="response-actions" title="Response Actions">
            <P>
              Beyond investigation, notables can trigger response actions. Every action runs behind a{' '}
              <strong>human approval gate</strong>. Nothing destructive happens automatically.
            </P>
            <Table
              headers={['Action', 'Behavior']}
              rows={[
                ['Notify', 'Send to a channel (email, Slack, webhook, PagerDuty).'],
                ['Create ticket', 'Open a ticket in your tracker with the notable context attached.'],
                ['Block IP', 'Opt-in per org; requires owner approval before execution.'],
                ['Disable user', 'Opt-in per org; requires owner approval before execution.'],
              ]}
            />
            <Callout type="warning">
              Destructive actions (block IP, disable user) are opt-in per organization and require
              explicit owner approval to run. They are disabled by default. Enable them only after
              configuring who may approve.
            </Callout>
          </SubSection>

          <SubSection id="playbooks" title="Playbooks">
            <P>
              Response actions can be composed into reusable <strong>playbooks</strong> that run when
              a notable matches their trigger conditions. For example, a brute-force playbook can
              chain "block the source IP" and "create a ticket", with the block still gated on owner
              approval, so a common response is one consistent, auditable workflow rather than a
              series of manual steps.
            </P>
          </SubSection>
        </Section>

        {/* MITRE */}
        <Section id="mitre" title="MITRE ATT&CK Coverage">
          <P>
            Because every detection and AI verdict is tagged with MITRE ATT&CK tactics and
            techniques, LogPulse renders a coverage heatmap derived from your currently enabled
            content. The heatmap shows which tactics and techniques are covered, helping you see and
            close gaps in your detection surface as you enable packs or add custom detections.
          </P>
        </Section>

        {/* Entity 360 */}
        <Section id="entity-360" title="Entity 360">
          <P>
            Entity 360 is a single unified view of an entity, a user, host, or IP, combining a
            security lens and an observability lens. The security lens shows accumulated risk, open
            notables, and IOC reputation; the observability lens shows the services, KPIs, and
            telemetry already tracked for that entity. The same view is reachable from both the
            security hub and the observability hub, so an analyst and an SRE arrive at the same
            full-context page from different directions.
          </P>
        </Section>

        {/* Compliance reporting */}
        <Section id="compliance" title="Compliance Reporting">
          <P>
            The SIEM produces control-coverage reports that map regulatory controls to the
            detections and data that evidence them. Supported frameworks include NIS2 (Article 21),
            DORA (ICT risk management), and ISO 27001. Each report shows which controls are evidenced
            by active detections and ingested data, and reports can be delivered on a schedule.
          </P>
          <Table
            headers={['Framework', 'Mapped To']}
            rows={[
              ['NIS2 (Art. 21)', 'Risk-management and incident-handling measures evidenced by detections and notables.'],
              ['DORA (ICT risk)', 'ICT risk-management and incident-reporting controls.'],
              ['ISO 27001', 'Annex A controls evidenced by monitoring, logging, and response content.'],
            ]}
          />
          <P>
            For the platform's broader certification status, DPA, sub-processors, and data residency,
            see the{' '}
            <Link to="/docs/platform/compliance" className="text-primary-600 dark:text-primary-400 hover:underline">
              Compliance &amp; Trust
            </Link>{' '}
            page.
          </P>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
